import React, { useEffect, useState, useRef } from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Grid2,
  Chip,
  Stack,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Box,
  Fab,
} from "@mui/material";
import actions from "../emuActions";
import authProvider from "../providers/authProvider";
import { useNotify } from "react-admin";
import { useNavigate } from "react-router-dom";
import CountdownTimer from "../components/CountdownTimer";
import WelcomeDialog from "../components/WelcomeDialog";
import TutorialDialog from "../components/TutorialDialog";

export default function DashboardPage() {
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);
  const [tutorialSubStep, setTutorialSubStep] = useState(0);
  const [tutorialCompleted, setTutorialCompleted] = useState(false);
  const [popover, setPopover] = useState(null);
  const [users, setUsers] = useState(null);
  const [demoDisabled, setDemoDisabled] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(10);
  const [isAdmin, setIsAdmin] = useState(false);
  const [welcomeOpened, setWelcomeOpened] = useState(false);

  const opelinSessionRef = useRef(null);
  const manSessionRef = useRef(null);
  const respSessionRef = useRef(null);

  const getSessionRef = sessionType => {
    const sessionMap = {
      opelin: opelinSessionRef,
      man: manSessionRef,
      resp: respSessionRef,
    };
    return sessionMap[sessionType] || null; // Restituisce null se la chiave non esiste
  };

  const defaultUsers = [
    { role: "OPERATOR", code: "opelin" },
    { role: "TECH_EXT", code: "amazon" },
    { role: "TECH_EXT_ADMIN", code: "demo" },
  ];

  const sleep = delay => new Promise(resolve => setTimeout(resolve, delay));
  const notify = useNotify();
  const navigate = useNavigate();

  useEffect(() => {
    authProvider
      .getDemo()
      .then(data => {
        let users = null;
        if (!data) {
          const connectedUser = authProvider.getUser();
          if (connectedUser.role === "SELLER_SIMPLEMAN") {
            users = [
              { role: "OPERATOR", code: "opelin" },
              { role: "TECH_EXT", code: "mandip" },
              { role: "TECH_EXT_ADMIN", code: "respdip" },
            ];
          } else {
            users = defaultUsers;
          }
          setSecondsRemaining(3600);
          setIsAdmin(true);
          setDemoDisabled(false);
        } else {
          setIsAdmin(false);
          users = data.users;
          setSecondsRemaining(data.live_seconds_remaining);
          if (data.live_seconds_remaining <= 0) {
            setDemoDisabled(true);
          }
          setTutorialCompleted(data.is_tutorial_completed === "1");
        }

        setUsers(users);
        initDevices(users).then(() => setDevicesLoading(false));
      })
      .catch(error => {
        notify(error.message, { type: "error" });
        setTimeout(function () {
          authProvider.logout();
          navigate("/login");
        }, 3000);
      });

    const n = window,
      i = document,
      o = i.getElementsByTagName("script")[0],
      t = i.createElement("script");
    t.src = "https://js.appetize.io/embed.js";
    t.async = 1;
    o.parentNode.insertBefore(t, o);
    const s = new Promise(function (e) {
      t.onload = function () {
        e();
      };
    });
    n.appetize = {
      getClient: function (...e) {
        return s.then(() => n.appetize.getClient(...e));
      },
    };
  }, []);

  const handleClick = (event, id) => {
    event.currentTarget.scrollIntoView({ behavior: "smooth", block: "start" });
    setPopover(id);
  };

  const handleClose = () => {
    setPopover(null);
  };

  const resetDevices = async () => {
    if (getSessionRef("opelin").current !== null) {
      await getSessionRef("opelin").current.end();
    }

    if (getSessionRef("man").current !== null) {
      await getSessionRef("man").current.end();
    }

    if (getSessionRef("resp").current !== null) {
      await getSessionRef("resp").current.end();
    }

    await sleep(2000);
  };

  const initDevices = async users => {
    if (demoDisabled) {
      notify("Demo scaduta o minuti di utilizzo terminati", { type: "error" });
      return;
    }

    if (secondsRemaining <= 0) {
      notify("Tempo di utilizzo degli smartphone terminato", { type: "error" });
      if (opelinSessionRef.current) {
        opelinSessionRef.current.end();
        opelinSessionRef.current = null;
      }
      if (manSessionRef.current) {
        manSessionRef.current.end();
        manSessionRef.current = null;
      }
      if (respSessionRef.current) {
        respSessionRef.current.end();
        respSessionRef.current = null;
      }
      return;
    }

    setDevicesLoading(true);

    let ocl = await window.appetize.getClient("#opelin");
    let sol = await ocl.startSession();
    sol.on("end", data => {
      opelinSessionRef.current = null;
    });

    sol.adbShellCommand(
      "content insert --uri content://settings/system --bind name:s:show_touches --bind value:i:1"
    );
    sol.adbShellCommand(
      'taskId=$(am stack list | grep -E -o "taskId=[0-9]+: $PACKAGE" | grep -E -o "[0-9]+"); am task lock $taskId'
    );
    sol.adbShellCommand("appops set com.android.systemui TOAST_WINDOW deny");

    fetch("/images/equipment1.jpeg").then(response => {
      response.blob().then(blob => {
        const file = new File([blob], "equipment1.jpeg", { type: blob.type });
        sol.addMedia(file);
      });
    });
    fetch("/images/equipment2.jpeg").then(response => {
      response.blob().then(blob => {
        const file = new File([blob], "equipment2.jpeg", { type: blob.type });
        sol.addMedia(file);
      });
    });

    opelinSessionRef.current = sol;
    let loginActions = actions["opelin"].map(a => {
      if (a.username || a.password) {
        let u = users.find(u => u.role === "OPERATOR");
        a.text = u.code;
      }
      return a;
    });
    sol.playActions(loginActions);

    let oman = await window.appetize.getClient("#man");
    let sman = await oman.startSession();
    sman.on("end", data => {
      manSessionRef.current = null;
    });

    sman.adbShellCommand(
      "content insert --uri content://settings/system --bind name:s:show_touches --bind value:i:1"
    );
    sman.adbShellCommand(
      'taskId=$(am stack list | grep -E -o "taskId=[0-9]+: $PACKAGE" | grep -E -o "[0-9]+"); am task lock $taskId'
    );
    sman.adbShellCommand("appops set com.android.systemui TOAST_WINDOW deny");

    fetch("/images/equipment1.jpeg").then(response => {
      response.blob().then(blob => {
        const file = new File([blob], "equipment1.jpeg", { type: blob.type });
        sman.addMedia(file);
      });
    });
    fetch("/images/equipment2.jpeg").then(response => {
      response.blob().then(blob => {
        const file = new File([blob], "equipment2.jpeg", { type: blob.type });
        sman.addMedia(file);
      });
    });

    manSessionRef.current = sman;
    loginActions = actions["man"].map(a => {
      if (a.username || a.password) {
        let u = users.find(u => u.role === "TECH_EXT");
        a.text = u.code;
      }
      return a;
    });
    sman.playActions(loginActions).catch(e => (document.location = ""));

    let ores = await window.appetize.getClient("#resp");
    let sresp = await ores.startSession();
    sresp.on("end", data => {
      respSessionRef.current = null;
    });

    sresp.adbShellCommand(
      "content insert --uri content://settings/system --bind name:s:show_touches --bind value:i:1"
    );
    sresp.adbShellCommand(
      'taskId=$(am stack list | grep -E -o "taskId=[0-9]+: $PACKAGE" | grep -E -o "[0-9]+"); am task lock $taskId'
    );
    sresp.adbShellCommand("appops set com.android.systemui TOAST_WINDOW deny");

    fetch("/images/equipment1.jpeg").then(response => {
      response.blob().then(blob => {
        const file = new File([blob], "equipment1.jpeg", { type: blob.type });
        sresp.addMedia(file);
      });
    });

    fetch("/images/equipment2.jpeg").then(response => {
      response.blob().then(blob => {
        const file = new File([blob], "equipment2.jpeg", { type: blob.type });
        sresp.addMedia(file);
      });
    });

    respSessionRef.current = sresp;
    loginActions = actions["resp"].map(a => {
      if (a.username || a.password) {
        let u = users.find(u => u.role === "TECH_EXT_ADMIN");
        a.text = u.code;
      }
      return a;
    });

    try {
      await sresp
        .playActions(loginActions)
        .catch(e => (document.location = ""));
    } catch (e) {
      document.location = "";
    }
  };

  return (
    <span style={styles.container}>
      <WelcomeDialog
        open={!welcomeOpened}
        secondsRemaining={secondsRemaining}
        loading={devicesLoading && !demoDisabled && !tutorialCompleted}
        onClose={() => {
          setWelcomeOpened(true);
        }}
      />

      <Box
        sx={{
          position: "fixed",
          bottom: 16,
          right: 16,
          zIndex: 9999,
        }}
      >
        <Fab
          color="primary"
          aria-label="countdown"
          sx={{
            width: 80,
            height: 80,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CountdownTimer
            isAdmin={isAdmin}
            initialTime={secondsRemaining}
            active={
              getSessionRef("opelin").current !== null ||
              getSessionRef("man").current !== null ||
              getSessionRef("resp").current !== null
            }
            onRefresh={timeLeft => {
              if (timeLeft <= 0) {
                if (getSessionRef("opelin").current != null) {
                  getSessionRef("opelin").current.end();
                }

                if (getSessionRef("man").current != null) {
                  getSessionRef("man").current.end();
                }

                if (getSessionRef("resp").current != null) {
                  getSessionRef("resp").current.end();
                }

                setDemoDisabled(true);
                handleClose();
                setTutorialSubStep(tutorialStep);
                setSecondsRemaining(0);
                setDevicesLoading(false);
              }

              // Lo faccio per rifare il rendering e stoppare il timer
              if (
                getSessionRef("opelin").current === null &&
                getSessionRef("man").current === null &&
                getSessionRef("resp").current === null
              ) {
                setSecondsRemaining(timeLeft);
              }
            }}
          />
        </Fab>
      </Box>

      <Grid2 container spacing={3}>
        <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="h6">Operatore di linea</Typography>
              <Typography variant="body2" color="textSecondary" height="120px">
                Il ruolo Operatore di linea puà² essere assegnato ad personam (se
                gli operatori sono muniti di smartphone personale) o a delle
                postazioni fisse (tablet o totem) poste sulla linea. L'operatore
                di linea puà² solo aprire richieste di intervento.
              </Typography>

              {users && (
                <Stack direction={"column"} spacing={2} style={{ margin: 5 }}>
                  <Chip
                    label={
                      <Typography variant="body2">
                        <b>Username</b>{" "}
                        {users.find(u => u.role === "OPERATOR").code}{" "}
                        <b>Password</b>{" "}
                        {users.find(u => u.role === "OPERATOR").code}
                      </Typography>
                    }
                    color="success"
                  />
                </Stack>
              )}

              <Grid2 container spacing={2} height={90}>
                <Grid2 size={6}>
                  <Button
                    className={
                      tutorialStep === 0 && popover != "new-rdi"
                        ? "blinking-button"
                        : ""
                    }
                    aria-describedby={"new-rdi"}
                    variant="contained"
                    color="primary"
                    style={{
                      ...styles.button,
                      ...{
                        backgroundColor:
                          tutorialStep !== 0 || demoDisabled
                            ? "#EFEFEF"
                            : "#EB5B2D",
                      },
                    }}
                    onClick={e => handleClick(e, "new-rdi")}
                    disabled={tutorialStep !== 0 || demoDisabled}
                  >
                    Crea nuova Richiesta
                  </Button>

                  <TutorialDialog
                    id={"new-rdi"}
                    open={popover == "new-rdi"}
                    target={document.getElementById("opelin")}
                    plac="right"
                    steps={[
                      "Clicca Nuova richiesta di intervento",
                      "Seleziona l'equipment dall'alberatura o tramite fotocamera scansionando il QR Code",
                      "Compila i campi principali",
                      "Scatta una foto o seleziona immagine dalla galleria (facoltativo)",
                      "Premi invia",
                    ]}
                    onFinishClick={() => {
                      setTutorialStep(tutorialStep + 1);
                      setPopover(null);
                    }}
                  ></TutorialDialog>
                </Grid2>

                <Grid2 size={6}>
                  <Button
                    className={
                      tutorialStep === 1 && popover != "view-rdi"
                        ? "blinking-button"
                        : ""
                    }
                    aria-describedby={"view-rdi"}
                    variant="contained"
                    color="primary"
                    style={{
                      ...styles.button,
                      ...{
                        backgroundColor:
                          tutorialStep !== 1 ? "#EFEFEF" : "#EB5B2D",
                      },
                    }}
                    onClick={e => handleClick(e, "view-rdi")}
                    disabled={tutorialStep !== 1 || demoDisabled}
                  >
                    Visualizza Richieste
                  </Button>

                  <TutorialDialog
                    id={"view-rdi"}
                    open={popover == "view-rdi"}
                    target={document.getElementById("opelin")}
                    plac="right"
                    steps={[
                      "Dal tab richieste di intervento, scrolla in basso per aggiornare",
                      "Clicca su una richiesta di intervento per visualizzare i dettagli",
                    ]}
                    onFinishClick={() => {
                      setTutorialStep(tutorialStep + 1);
                      setPopover(null);
                    }}
                  ></TutorialDialog>
                </Grid2>
              </Grid2>
              <div
                style={{
                  width: "100%",
                  alignItems: "center",
                  flexDirection: "row",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <iframe
                  id="opelin"
                  src="https://appetize.io/embed/b_o6kvpn3s4w4abk7fyuyv5ez7iy"
                  frameborder="0"
                  width="310"
                  height="650"
                  title="Operatore di linea"
                  style={{
                    pointerEvents:
                      demoDisabled || secondsRemaining <= 0 ? "none" : "auto",
                  }}
                ></iframe>
              </div>
            </CardContent>
          </Card>
        </Grid2>

        <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="h6">Responsabile di Manutenzione</Typography>
              <Typography variant="body2" color="textSecondary" height="120px">
                Il responsabile di manutenzione ha il suo account personale. Puà²
                visualizzare tutto, puà² assegnare le richieste di intervento ai
                manutentori. Puà² inoltre visualizzare tutta la reportistica.
              </Typography>

              {users && (
                <Stack direction={"column"} spacing={2} style={{ margin: 5 }}>
                  <Chip
                    label={
                      <Typography variant="body2">
                        <b>Username</b>{" "}
                        {users.find(u => u.role === "TECH_EXT_ADMIN").code}{" "}
                        <b>Password</b>{" "}
                        {users.find(u => u.role === "TECH_EXT_ADMIN").code}
                      </Typography>
                    }
                    color="success"
                  />
                </Stack>
              )}

              <Grid2 container spacing={2} height={90}>
                <Grid2 size={3}>
                  <Button
                    className={
                      tutorialStep === 2 && popover != "assign"
                        ? "blinking-button"
                        : ""
                    }
                    aria-describedby={"assign"}
                    variant="contained"
                    color="primary"
                    style={{
                      ...styles.button,
                      ...{
                        backgroundColor:
                          tutorialStep !== 2 ? "#EFEFEF" : "#EB5B2D",
                      },
                    }}
                    onClick={e => handleClick(e, "assign")}
                    disabled={tutorialStep !== 2 || demoDisabled}
                  >
                    Assegna Richieste
                  </Button>

                  <TutorialDialog
                    id={"assign"}
                    open={popover == "assign"}
                    target={document.getElementById("resp")}
                    plac="right"
                    steps={[
                      "Clicca Simpleman nella home",
                      "Fare click sul tab Richieste di intervento",
                      "Scrolla in basso per aggiornare",
                      "Fare click su ASSEGNA sulla richiesta da assegnare",
                      "Selezionare il manutentore",
                      "Fare click su Assegna Intervento",
                    ]}
                    onFinishClick={() => {
                      setTutorialStep(tutorialStep + 1);
                      setPopover(null);
                    }}
                  ></TutorialDialog>
                </Grid2>

                <Grid2 size={3}>
                  <Button
                    className={
                      tutorialStep === 5 && popover != "activities"
                        ? "blinking-button"
                        : ""
                    }
                    aria-describedby={"activities"}
                    variant="contained"
                    color="primary"
                    style={{
                      ...styles.button,
                      ...{
                        backgroundColor:
                          tutorialStep !== 5 ? "#EFEFEF" : "#EB5B2D",
                      },
                    }}
                    onClick={e => handleClick(e, "activities")}
                    disabled={tutorialStep !== 5 || demoDisabled}
                  >
                    Visualizza attività 
                  </Button>

                  <TutorialDialog
                    id={"activities"}
                    open={popover == "activities"}
                    target={document.getElementById("resp")}
                    plac="right"
                    steps={[
                      "Fare click sul tab Attività ",
                      "Scrolla in basso per aggiornare la lista delle attività ",
                      "Clicca sull'attività  (se presente) per visualizzare il dettaglio",
                    ]}
                    onFinishClick={() => {
                      setTutorialStep(tutorialStep + 1);
                      setPopover(null);
                    }}
                  ></TutorialDialog>
                </Grid2>

                <Grid2 size={3}>
                  <Button
                    className={
                      tutorialStep === 6 && popover != "reports"
                        ? "blinking-button"
                        : ""
                    }
                    aria-describedby={"reports"}
                    variant="contained"
                    color="primary"
                    style={{
                      ...styles.button,
                      ...{
                        backgroundColor:
                          tutorialStep !== 6 ? "#EFEFEF" : "#EB5B2D",
                      },
                    }}
                    onClick={e => handleClick(e, "reports")}
                    disabled={tutorialStep !== 6 || demoDisabled}
                  >
                    Visualizza rapportini
                  </Button>

                  <TutorialDialog
                    id={"reports"}
                    open={popover == "reports"}
                    target={document.getElementById("resp")}
                    plac="right"
                    steps={[
                      "Fare click sul tab Rapportini",
                      "Scrolla in basso per aggiornare la lista",
                      "Clicca sul box per aprire i dettagli del rapportino",
                    ]}
                    onFinishClick={() => {
                      setTutorialStep(tutorialStep + 1);
                      setPopover(null);
                    }}
                  ></TutorialDialog>
                </Grid2>

                <Grid2 size={3}>
                  <Button
                    className={
                      tutorialStep === 7 && popover != "reports"
                        ? "blinking-button"
                        : ""
                    }
                    aria-describedby={"stats"}
                    variant="contained"
                    color="primary"
                    style={{
                      ...styles.button,
                      ...{
                        backgroundColor:
                          tutorialStep !== 7 ? "#EFEFEF" : "#EB5B2D",
                      },
                    }}
                    onClick={e => handleClick(e, "stats")}
                    disabled={tutorialStep !== 7 || demoDisabled}
                  >
                    Visualizza statistiche
                  </Button>

                  <TutorialDialog
                    id={"stats"}
                    open={popover == "stats"}
                    target={document.getElementById("resp")}
                    plac="right"
                    steps={[
                      "Fare click sul tab Statistiche",
                      "Scorri la pagina per visualizzare le statistiche rapide",
                    ]}
                    onFinishClick={() => {
                      setTutorialStep(tutorialStep + 1);
                      setPopover(null);

                      setTutorialCompleted(true);
                      authProvider.setTutorialCompleted();
                      notify(
                        "Hai completato il tutorial guidato. Puoi ora procedere all'utilizzo con la modalità  manuale.",
                        { type: "success", autoHideDuration: 10000 }
                      );
                    }}
                  ></TutorialDialog>
                </Grid2>
              </Grid2>

              <div
                style={{
                  width: "100%",
                  alignItems: "center",
                  flexDirection: "row",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <iframe
                  id="resp"
                  src="https://appetize.io/embed/b_o6kvpn3s4w4abk7fyuyv5ez7iy"
                  frameborder="0"
                  width="310"
                  height="650"
                  title="Responsabile di Manutenzione"
                  style={{
                    pointerEvents:
                      demoDisabled || secondsRemaining <= 0 ? "none" : "auto",
                  }}
                ></iframe>
              </div>
            </CardContent>
          </Card>
        </Grid2>

        <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="h6">Manutentore</Typography>
              <Typography variant="body2" color="textSecondary" height="120px">
                Il ruolo manutentore è assegnato ad personam: ogni manutentore
                ha il proprio account personale ed accede con il suo smartphone.
                Il manutentore puà² prendere in carico le richieste di
                intervento, visualizzare documenti ed effettuare rapportini.
              </Typography>

              {users && (
                <Stack direction={"column"} spacing={2} style={{ margin: 5 }}>
                  <Chip
                    label={
                      <Typography variant="body2">
                        <b>Username</b>{" "}
                        {users.find(u => u.role === "TECH_EXT").code}{" "}
                        <b>Password</b>{" "}
                        {users.find(u => u.role === "TECH_EXT").code}
                      </Typography>
                    }
                    color="success"
                  />
                </Stack>
              )}

              <Grid2 container spacing={2} height={90}>
                <Grid2 size={6}>
                  <Button
                    className={
                      tutorialStep === 3 && popover != "start"
                        ? "blinking-button"
                        : ""
                    }
                    aria-describedby={"start"}
                    variant="contained"
                    color="primary"
                    style={{
                      ...styles.button,
                      ...{
                        backgroundColor:
                          tutorialStep !== 3 ? "#EFEFEF" : "#EB5B2D",
                      },
                    }}
                    onClick={e => handleClick(e, "start")}
                    disabled={tutorialStep !== 3 || demoDisabled}
                  >
                    Avvia attività 
                  </Button>

                  <TutorialDialog
                    id={"start"}
                    open={popover == "start"}
                    target={document.getElementById("man")}
                    plac="left"
                    steps={[
                      "Clicca Simpleman nella home per visualizzare il riepilogo",
                      "Scrollare verso il basso per aggiornare",
                      "Cliccare avvia attività  in corrispondenza dell'attività  da iniziare",
                      "Premere il tasto START",
                    ]}
                    onFinishClick={() => {
                      setTutorialStep(tutorialStep + 1);
                      setPopover(null);
                    }}
                  ></TutorialDialog>
                </Grid2>

                <Grid2 size={6}>
                  <Button
                    className={
                      tutorialStep === 4 && popover != "report"
                        ? "blinking-button"
                        : ""
                    }
                    aria-describedby={"report"}
                    variant="contained"
                    color="primary"
                    style={{
                      ...styles.button,
                      ...{
                        backgroundColor:
                          tutorialStep !== 4 ? "#EFEFEF" : "#EB5B2D",
                      },
                    }}
                    onClick={e => handleClick(e, "report", "man")}
                    disabled={tutorialStep !== 4 || demoDisabled}
                  >
                    Rapportino
                  </Button>

                  <TutorialDialog
                    id={"report"}
                    open={popover == "report"}
                    target={document.getElementById("man")}
                    plac="left"
                    steps={[
                      "Selezionare il tab Riepilogo",
                      "Selezione la voce In Lavorazione (sotto il titolo Riepilogo)",
                      "Scrollare verso il basso per aggiornare",
                      "Clicca Dettagli in corrispondenza dell'attività  da completare",
                      "Clicca sul rapportino avviato in precedenza (sotto il titolo Rapportini è presente l'elenco, cliccare sulla freccetta blu)",
                      "Compila le informazioni",
                      "Specifica ricambi utilizzati (facoltativo)",
                      "Scatta una foto o scegli una immagine dalla galleria (facoltativo) e compila le ultime informazioni",
                      "Specifica collaudo effettuato SI (obbligatorio per chiusura attività )",
                      "Clicca Termina intervento",
                    ]}
                    onFinishClick={() => {
                      setTutorialStep(tutorialStep + 1);
                      setPopover(null);
                    }}
                  ></TutorialDialog>
                </Grid2>
              </Grid2>
              <div
                style={{
                  width: "100%",
                  alignItems: "center",
                  flexDirection: "row",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <iframe
                  id="man"
                  src="https://appetize.io/embed/b_o6kvpn3s4w4abk7fyuyv5ez7iy"
                  frameborder="0"
                  title="Manutentore"
                  width="310"
                  height="650"
                  style={{
                    pointerEvents:
                      demoDisabled || secondsRemaining <= 0 ? "none" : "auto",
                  }}
                ></iframe>
              </div>
            </CardContent>
          </Card>
        </Grid2>
      </Grid2>

      <Dialog open={demoDisabled}>
        <DialogTitle id="alert-dialog-title">
          {"Tempo di utilizzo terminato"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Hai esaurito il tempo per utilizzare gli smartphone nella modalità 
            demo. Contatta il commerciale per un'estensione personalizzata o per
            maggiori informazioni.
          </DialogContentText>

          <DialogContentText>
            <b>Email</b> p.dipede@simpleman.it
          </DialogContentText>

          <DialogContentText>
            <b>Numero di telefono</b> 388 824 7836
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={e => {
              window.location = "mailto:p.dipede@simpleman.it";
              e.preventDefault();
            }}
          >
            Contatta il commerciale
          </Button>
          <Button onClick={() => setDemoDisabled(false)}>OK</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={tutorialCompleted}>
        <DialogTitle id="alert-dialog-title">
          {"Tutorial completato"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Hai completato tutti i passaggi guidati del tutorial. Puoi procedere
            autonomamente all'utilizzo degli smartphone fino alla scadenza del
            tempo residuo.
          </DialogContentText>

          <DialogContentText>
            <b>Email</b> p.dipede@simpleman.it
          </DialogContentText>

          <DialogContentText>
            <b>Numero di telefono</b> 388 824 7836
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            autoFocus
            onClick={e => {
              window.location = "mailto:p.dipede@simpleman.it";
              e.preventDefault();
            }}
          >
            Contatta il commerciale
          </Button>
          <Button onClick={() => setTutorialCompleted(false)}>OK</Button>
        </DialogActions>
      </Dialog>
    </span>
  );
}

const styles = {
  container: {
    flexDirection: "column",
    alignItems: "center",
    minHeight: "100vh",
    padding: "20px",
    display: "flex",
  },
  card: {
    textAlign: "center",
    padding: "10px",
  },
  button: {
    marginTop: "10px",
    backgroundColor: "#EB5B2D",
    fontWeight: "bold",
    width: "100%",
  },
  overlay: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100vh",
    backgroundColor: "rgba(128, 128, 128, 0.95)", // Sfondo grigio semi-trasparente
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000, // Assicura che il loader sia sopra tutto
    flexDirection: "column",
  },
  loader: {
    color: "white",
  },
  subStepSelected: {
    backgroundColor: "#EB5B2D",
    color: "white",
    fontWeight: "bold",
  },
  divider: {
    p: 0,
    width: "100%",
    maxWidth: 360,
    border: "1px solid",
    borderColor: "divider",
    backgroundColor: "background.paper",
  },
};